import { ReplayOutlined } from '@mui/icons-material'
import { IconButton, useTheme } from '@mui/material'
import { SearchTextField } from 'components/OrganizationSettings/VirtualFactory/Capabilities/WorkingStepsSurchargesAndDiscounts/components/SearchTextField'
import { deleteBomItemAttachment } from 'features/BillOfMaterials/store/asyncActions/deleteAttachment'
import { downloadAttachment } from 'features/BillOfMaterials/store/asyncActions/downloadAttachment'
import { fetchCurrentProject } from 'features/BillOfMaterials/store/asyncActions/fetchProject'
import { projectSelectors } from 'features/BillOfMaterials/store/selectors/projectSelectors'
import { useBomItemDetailsDialog } from 'features/BomItemDetailsDialog/useBomItemDetailsDialog'
import { isEqual } from 'lodash'
import { matchSorter } from 'match-sorter'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BoMItemAttachmentDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import { useAppDispatch, useAppSelector } from 'store/configureStore'
import { AttachmentCards } from './AttachmentCards'
import { AttachmentList } from './AttachmentList'

type Props = {
  bomItemPointer: BomItemPointer
  initialFilesToUpload: File[]
  variant: 'list' | 'cards'
  noTitle?: boolean
}

export const BomItemAttachmentDetails = (props: Props) => {
  const theme = useTheme()
  const { t } = useTranslation()

  const attachments = useAppSelector(
    projectSelectors.bomItemAttachmentSelector(props.bomItemPointer),
    isEqual,
  )

  const [filteredAttachments, setFilteredAttachments] =
    useState<BoMItemAttachmentDto[]>(attachments)

  useEffect(() => {
    setFilteredAttachments(attachments)
  }, [attachments])

  const [filterValue, setFilterValue] = useState('')

  const handleSetFilterValue = (value: string) => {
    setFilterValue(value)

    const filtered = matchSorter(attachments, value, {
      keys: ['originalFileName'],
    })

    setFilteredAttachments(filtered)
  }

  const dispatch = useAppDispatch()

  const handleDeleteAttachments = useCallback(
    (attachment: BoMItemAttachmentDto) => {
      dispatch(
        deleteBomItemAttachment({
          bomItemPointer: props.bomItemPointer,
          fileName: [attachment.fileName],
          inlineConfirmation: true,
        }),
      )
    },
    [dispatch, props.bomItemPointer],
  )

  const handleDownloadAttachment = useCallback(
    (attachment: BoMItemAttachmentDto) => {
      dispatch(
        downloadAttachment({
          bomItemId: props.bomItemPointer.id,
          fileName: attachment.fileName,
        }),
      )
    },
    [dispatch, props.bomItemPointer.id],
  )

  const previewRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const abortController = new AbortController()

    previewRef.current?.addEventListener(
      'wheel',
      (e) => {
        e.stopPropagation()
        e.preventDefault()
      },
      { passive: false, signal: abortController.signal },
    )

    return () => {
      abortController.abort()
    }
  }, [])

  const openDetailsDialog = useBomItemDetailsDialog()

  const handleOpenDrawings = useCallback(
    (attachmentId: string) => {
      openDetailsDialog('drawings', {
        bomItemPointer: props.bomItemPointer,
        initialAttachmentId: attachmentId,
      })
    },
    [openDetailsDialog, props.bomItemPointer],
  )

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        height: 'calc(100% - 80px)',
        justifyContent: 'space-between',
        paddingTop: theme.spacing(2),
        overflow: 'auto',
      }}
    >
      {!attachments?.length ? null : (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-end',
              gap: '.5rem',
              width: '100%',
            }}
          >
            <SearchTextField
              currentQuery={filterValue}
              updateQuery={handleSetFilterValue}
            />
            <IconButton
              size="small"
              onClick={() => {
                dispatch(fetchCurrentProject({}))
              }}
            >
              <ReplayOutlined fontSize="small" />
            </IconButton>
          </div>
          {props.variant === 'list' ? (
            <div style={{ height: '100%' }}>
              <AttachmentList
                filteredAttachments={filteredAttachments}
                bomItemPointer={props.bomItemPointer}
                onDelete={handleDeleteAttachments}
                onDownload={handleDownloadAttachment}
                onOpenDrawings={handleOpenDrawings}
                noTitle={props.noTitle}
              />
            </div>
          ) : (
            <div style={{ height: '100%' }}>
              <AttachmentCards
                filteredAttachments={filteredAttachments}
                bomItemPointer={props.bomItemPointer}
                onDelete={handleDeleteAttachments}
                onDownload={handleDownloadAttachment}
                onOpenDrawings={handleOpenDrawings}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}
